import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import CountdownTimer from "../components/CountdownTimer";
import WorldMap from "../components/WorldMap";
import {
  Search,
  Filter,
  MapPin,
  TrendingUp,
  Coins,
  ArrowRight,
  FileCheck,
  Clock,
  Rocket
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, mapRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/map-data`)
      ]);
      setProjects(projectsRes.data);
      setMapData(mapRes.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getScoreColor = (score) => {
    if (score >= 800) return "text-emerald-600 bg-emerald-50 border-emerald-200";
    if (score >= 600) return "text-blue-600 bg-blue-50 border-blue-200";
    if (score >= 400) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const getScoreBg = (score) => {
    if (score >= 800) return "bg-emerald-500";
    if (score >= 600) return "bg-blue-500";
    if (score >= 400) return "bg-amber-500";
    return "bg-red-500";
  };

  const filteredProjects = projects.filter((project) => {
    const matchesStatus = selectedStatus === "all" || project.status === selectedStatus;
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          project.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">STO Projects</h1>
        <p className="text-slate-500 mt-1">AI-powered due diligence scoring for investor confidence</p>
      </div>

      {/* Project Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <p className="text-sm text-slate-500">Total Projects</p>
          <p className="text-2xl font-bold text-slate-900">{projects.length}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <p className="text-sm text-slate-500">Token Price</p>
          <p className="text-2xl font-bold text-slate-900">$986</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <p className="text-sm text-slate-500">Tokens Per Project</p>
          <p className="text-2xl font-bold text-slate-900">100,000</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <p className="text-sm text-slate-500">Launching Soon</p>
          <p className="text-2xl font-bold text-[#3B82F6]">{projects.filter(p => p.status === 'launching').length}</p>
        </div>
      </div>

      {/* Launch Countdowns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {projects.map((project) => (
          <CountdownTimer
            key={project.id}
            launchDate={project.launch_date}
            projectName={project.name}
          />
        ))}
      </div>

      {/* World Map */}
      {mapData && <WorldMap projects={mapData.projects} />}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-projects"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="launching">Launching Soon</SelectItem>
            <SelectItem value="active">Active</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredProjects.map((project) => {
          const score = project.score_data?.score || 0;
          
          return (
            <Card
              key={project.id}
              className="border-slate-200 overflow-hidden cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all group"
              onClick={() => navigate(`/projects/${project.id}`)}
              data-testid={`project-card-${project.id}`}
            >
              <div className="relative">
                <img
                  src={project.image_url}
                  alt={project.name}
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="bg-[#0F2744] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <Rocket className="w-3 h-3" />
                    Launching Soon
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <div className={`px-3 py-1 rounded-full text-sm font-bold border ${getScoreColor(score)}`}>
                    {score}/1000
                  </div>
                </div>
                <div className="absolute bottom-3 right-3">
                  <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-slate-700">
                    {project.token_symbol}
                  </span>
                </div>
              </div>
              
              <CardContent className="p-4">
                <div className="mb-3">
                  <h3 className="font-semibold text-slate-900 text-lg group-hover:text-[#3B82F6] transition-colors">
                    {project.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="industry-tag">{project.industry}</span>
                    <span className="flex items-center text-xs text-slate-500">
                      <MapPin className="w-3 h-3 mr-1" />
                      {project.location}
                    </span>
                  </div>
                </div>

                {/* Score Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-500 flex items-center gap-1">
                      <FileCheck className="w-4 h-4" />
                      Document Score
                    </span>
                    <span className={`font-semibold ${score >= 600 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {project.score_data?.completion_percentage || 0}% Complete
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${getScoreBg(score)}`}
                      style={{ width: `${(score / 1000) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Countdown */}
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Launch Countdown</span>
                    <CountdownTimer launchDate={project.launch_date} projectName={project.name} compact />
                  </div>
                </div>

                <p className="text-sm text-slate-600 line-clamp-2 mb-4">{project.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-slate-500">Token Price</p>
                    <p className="font-semibold text-slate-900">{formatCurrency(project.token_price)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Expected ROI</p>
                    <p className="font-semibold text-emerald-600 flex items-center">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      {project.roi_percentage}%
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="flex items-center text-sm text-slate-600">
                    <Coins className="w-4 h-4 mr-1" />
                    {project.token_supply.toLocaleString()} tokens
                  </div>
                  <span className="text-[#3B82F6] font-medium text-sm flex items-center group-hover:translate-x-1 transition-transform">
                    View Details
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500">No projects found matching your criteria.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setSelectedStatus("all");
              setSearchQuery("");
            }}
            data-testid="clear-all-filters"
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
}
